import React from 'react';
import { TableCell, Stack, Chip, Tooltip, Box, Typography, useTheme, alpha } from '@mui/material';
import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { Worker, WorkerStatus } from '../../../models/worker';

const fmt = (t: string) => t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());

/** Recruitment pipeline stage, plus a warning when a profile is incomplete for its stage. */
const WorkerStatusCell: React.FC<{ worker: Worker }> = ({ worker }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const readiness = worker.candidateReadiness;
    const missingCandidateFields = readiness?.missingFields ?? [];
    const statusesRequiringCompletionReminder: WorkerStatus[] = [WorkerStatus.INCOMPLETE, WorkerStatus.PENDING];
    const showCandidateWarning =
        statusesRequiringCompletionReminder.includes(worker.status as WorkerStatus) &&
        !!readiness &&
        !readiness.isComplete &&
        missingCandidateFields.length > 0;
    const candidateWarningTooltip = (
        <Box sx={{ p: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Missing before candidate:
            </Typography>
            {missingCandidateFields.map((field) => (
                <Typography key={field} variant="caption" display="block">
                    • {field}
                </Typography>
            ))}
        </Box>
    );

    const statusColors: Record<string, { bg: string; fg: string }> = {
        INCOMPLETE: { bg: alpha(theme.palette.error.main, isDark ? 0.3 : 0.1), fg: theme.palette.error.main },
        PENDING: { bg: alpha(theme.palette.warning.main, isDark ? 0.3 : 0.1), fg: theme.palette.warning.main },
        CANDIDATE: { bg: alpha(theme.palette.success.main, isDark ? 0.3 : 0.1), fg: theme.palette.success.main },
        PLACED: { bg: alpha(theme.palette.info.main, isDark ? 0.3 : 0.1), fg: theme.palette.info.main },
        REJECTED: { bg: alpha('#e91e63', isDark ? 0.3 : 0.1), fg: '#e91e63' },
        BLOCKED: { bg: alpha('#795548', isDark ? 0.3 : 0.1), fg: '#795548' },
        RESERVE_POOL: { bg: alpha(theme.palette.secondary.main, isDark ? 0.3 : 0.1), fg: theme.palette.secondary.main },
    };
    const fallback = { bg: alpha(theme.palette.text.secondary, 0.1), fg: theme.palette.text.secondary };
    const sc = statusColors[worker.status] || fallback;

    return (
        <TableCell>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                {showCandidateWarning && (
                    <Tooltip title={candidateWarningTooltip} arrow placement="top">
                        <WarningAmberIcon fontSize="small" sx={{ color: theme.palette.warning.main, flexShrink: 0 }} />
                    </Tooltip>
                )}
                <Chip
                    label={fmt(worker.status)}
                    size="small"
                    sx={{ backgroundColor: sc.bg, color: sc.fg, fontWeight: 600, height: 24, '& .MuiChip-label': { px: 1.5 } }}
                />
                {worker.lifecycle?.cvCreated && <Chip label="CV created" size="small" variant="outlined" />}
                {worker.lifecycle?.offeredToClient && <Chip label="Offered" size="small" variant="outlined" color="info" />}
                {worker.lifecycle?.contacted && <Chip label="Contacted" size="small" variant="outlined" color="success" />}
            </Stack>
        </TableCell>
    );
};

export default WorkerStatusCell;
